import { defineAsyncComponent, type App } from "vue";

// Here we register all Vue components that can be injected by the backend code as mia-* tags.
// Please keep this list sorted to avoid merge conflicts.
import AjaxConfigRegistration from "@/Control/Vue/AjaxConfigRegistration";
import AmplitudeLinkTagger from "@/tracking/AmplitudeLinkTagger";
import AppContainer from "@/AppContainer.vue";
import AttachedItem from "@/Control/Vue/AttachedItem.vue";
import Banner from "@/common/Banner.vue";
import Button from "@/Control/Vue/Button.vue";
import ConversionStoreRegistration from "@/Control/Vue/Stores/VueList/ConversionStoreRegistration";
import Freshdesk from "@/Parents/Control/Vue/Freshdesk";
import I18NextStoreRegistration from "@/Control/Vue/Stores/I18NextStoreRegistration";
import ListStoreRegistration from "@/Control/Vue/Stores/VueList/ListStoreRegistration";
import Logo from "@/layout/common/Logo.vue";
import Modal from "@/Control/Vue/Notifications/Modal.vue";
import Notifier from "@/Control/Vue/Notifications/Shared/Notifier";
import ScriptManager from "@/Control/Vue/Tracking/ScriptManager";
import SelectedItemStoreRegistration from "@/Control/Vue/Stores/SelectedItemStoreRegistration.vue";
import VimeoPlayer from "@/Control/Vue/VimeoPlayer.vue";
import VueList from "@/Control/Vue/Lists/VueList/VueList.vue";

import {
	FeatureLevelHint,
	FloatingHelpButton,
	MiaTour,
	MiaTourStoreRegistration,
	ResourceLinksDropdown,
	ResourceLinksExpandable,
	StudentElection,
	ToggleableHelpBoxWrapper,
	TutorialVideosPlayButton,
} from "@/chunks/LoggedIn";
import {
	CastleUpgrade,
	DesignStudioEditorHeader,
	DesignStudioTools,
	GameHeader,
	MemberMiniworld,
	MiniWorldHouseCompetitionSidebar,
	MyWorldRoot,
	RewardVimeoPlayer,
} from "@/chunks/MyWorld";
import {
	AssessmentReview,
	Vocabulary,
} from "@/chunks/PracticeAndAssessment";
import {
	ActiveModerators,
	AdminCouponCodeEditor,
	AdminCustomGameEditor,
	AdminLostPetSearch,
	AdminManageMembership,
	AdminReviewUserUploadedVideo,
	AdminTextResourceSearch,
	AnswerAttempts,
	AwardButton,
	CheckpointReports,
	ChildPenalties,
	ClassWalletInvoiceReport,
	CompetitionLeaders,
	ContentSynchronization,
	ContestDataExport,
	CourseProgressTransfer,
	DownloadEmails,
	FailedSubscriptionPauses,
	FeatureLevelDropdown,
	GiftCodeGenerator,
	JavaScriptErrorTester,
	KlaviyoSync,
	LessonAssessment,
	LessonFeedback,
	LessonPractice,
	ManageBozoMembers,
	ModeratorsNote,
	NotificationTesterHeading,
	OnlineSchoolRoot,
	PricingEditor,
	RegisterMember,
	RegulatorSections,
	RoutingAffinityTester,
	ScalableRoutes,
	SiteSlowWarning,
	SpecialUsers,
	ToggleableControl,
	TransferTextResourcesPreview,
	VirtualCurrencyCompetition,
	VirtualCurrencyMonitor,
	VueGame,
} from "@/chunks/Admin";
import {
	AddStudentButton,
	AssessmentAttemptNotificationPanel,
	AttendanceReport,
	Checkout,
	StudentStatusBar,
	CurrencyReward,
	CurriculumRoot,
	DateSelector,
	FinalAttemptFailingMinimumPassingGradeNotificationPanel,
	GlyphiconMenu,
	GradebookRoot,
	Iframe,
	InformationBanner,
	ManageMembershipRoot,
	ManagePaymentMethod,
	MembershipReceipts,
	MiaPrepBanner,
	MiaprepOnlineAcademyOhioBanner,
	NewsletterPreviewBanner,
	OnlineSchoolAcademicPlans,
	ParentReviewedUserUploadedVideo,
	ParentReviewUserUploadedVideo,
	ParentsChildrenSelector,
	ParentsNavbar,
	ParentUserUploadedVideoTermsModal,
	PauseMembership,
	PauseMembershipDashboard,
	PaymentRequiresAttentionBanner,
	ReportSearch,
	SingleSubjectMembershipDashboard,
	SiteGuidelines,
	TaskCounterPanel,
	TogglePanel,
	UnpauseMembership,
} from "@/chunks/Parents";
import {
	AccessibilityInfoPanel,
	CoverVideoPanel,
	CoverVideoPlayButton,
	FAQ,
	ForgotPasswordParentPage,
	ForgotPasswordStudentPage,
	KlaviyoFormPlaceholder,
	LandingPage,
	LoginParentPage,
	LoginStudentPage,
	MembershipDescription,
	MembershipFlipTiles,
	MiaPrepAnnouncement,
	NavbarBlog,
	OnlineSchoolLoginSelectPage,
	PricingPanel,
	RegistrationConsentPage,
	RegistrationStudentSelectionPage,
	RewardAndSocialFeaturesPanel,
	ScopeAndSequence,
	ScopeAndSequenceButton,
	ScopeAndSequencePanel,
	SiteAbout,
	SiteContactTypeForm,
	SiteImpressum,
	StandardFooter,
	SubjectPreview,
	TestimonialsPanel,
	TrialPlanSelectionPage,
} from "@/chunks/Public";
import {
	AnnouncementPanel,
	CampaignAccessoryGiveawaySidebar,
	Funds,
	Header,
	RulesModal,
	ScoopCompetitionSidebar,
	ShopCompetitionSidebar,
	ShoutBox,
	SmileySelector,
	StreakDashboard,
	StreakRegistration,
	StreakSidebar,
	StudentAnnouncement,
	TeamAchievementsCompetitionSidebar,
} from "@/chunks/Students";
import {
	LearningPathAssignments,
	LearningPathCompleted,
	Lesson,
	LessonGameHeader,
	LessonsRoot,
	LevelUpScreen,
	TypingGameTouchDevices,
} from "@/chunks/LearningPath";
import {
	MemberSearch,
	MemberUnblockList,
	MemberUnfriendList,
} from "@/chunks/MyFriends";
import {
	GuestBookList,
	MiniMeCloset,
	MiniMeClosetBanner,
	NewspaperEditionSidebar,
	NewspaperPreviewSearch,
	ProfileAvatarBanner,
	TeamSearch,
} from "@/chunks/Community";
import {
	MoreVideosSection,
	UserUploadedVideos,
	UserUploadedVimeoPlayer,
	VideoRoot,
	VideoWatchHeading,
	VimeoUploadForm,
} from "@/chunks/Videos";

export function registerMiaComponents(app: App<Element>): void {
	// Please keep this list sorted to avoid merge conflicts.
	app.component("mia-accessibilityinfopanel", AccessibilityInfoPanel);
	app.component("mia-activemoderators", ActiveModerators);
	app.component("mia-addstudentbutton", AddStudentButton);
	app.component("mia-admincouponcodeeditor", AdminCouponCodeEditor);
	app.component("mia-admincustomgameeditor", AdminCustomGameEditor);
	app.component("mia-adminlostpetsearch", AdminLostPetSearch);
	app.component("mia-adminmanagemembership", AdminManageMembership);
	app.component("mia-adminreviewuseruploadedvideo", AdminReviewUserUploadedVideo);
	app.component("mia-admintextresourcesearch", AdminTextResourceSearch);
	app.component("mia-ajaxconfigregistration", AjaxConfigRegistration);
	app.component("mia-amplitudelinktagger", AmplitudeLinkTagger);
	app.component("mia-announcementpanel", AnnouncementPanel);
	app.component("mia-answerattempts", AnswerAttempts);
	app.component("mia-appcontainer", AppContainer);
	app.component("mia-assessment", LessonAssessment);
	app.component("mia-assessmentattemptnotificationpanel", AssessmentAttemptNotificationPanel);
	app.component("mia-assessmentreview", AssessmentReview);
	app.component("mia-attacheditem", AttachedItem);
	app.component("mia-attendancereport", AttendanceReport);
	app.component("mia-awardbutton", AwardButton);
	app.component("mia-banner", Banner);
	app.component("mia-button", Button);
	app.component("mia-campaignaccessorygiveawaysidebar", CampaignAccessoryGiveawaySidebar);
	app.component("mia-castleupgrade", CastleUpgrade);
	app.component("mia-checkout", Checkout);
	app.component("mia-checkpointreports", CheckpointReports);
	app.component("mia-childpenalties", ChildPenalties);
	app.component("mia-studentstatusbar", StudentStatusBar);
	app.component("mia-classwalletinvoicereport", ClassWalletInvoiceReport);
	app.component("mia-competitionleaders", CompetitionLeaders);
	app.component("mia-contentsynchronization", ContentSynchronization);
	app.component("mia-contestdataexport", ContestDataExport);
	app.component("mia-conversionstoreregistration", ConversionStoreRegistration);
	app.component("mia-courseprogresstransfer", CourseProgressTransfer);
	app.component("mia-covervideopanel", CoverVideoPanel);
	app.component("mia-covervideoplaybutton", CoverVideoPlayButton);
	app.component("mia-currencyreward", CurrencyReward);
	app.component("mia-curriculumroot", CurriculumRoot);
	app.component("mia-dateselector", DateSelector);
	app.component("mia-designstudioeditorheader", DesignStudioEditorHeader);
	app.component("mia-designstudiotools", DesignStudioTools);
	app.component("mia-downloademails", DownloadEmails);
	app.component("mia-errorpage", defineAsyncComponent(() => import("@/Control/Vue/ErrorPage.vue")));
	app.component("mia-failedsubscriptionpauses", FailedSubscriptionPauses);
	app.component("mia-faq", FAQ);
	app.component("mia-featureleveldropdown", FeatureLevelDropdown);
	app.component("mia-featurelevelhint", FeatureLevelHint);
	app.component("mia-finalattemptfailingminimumpassinggradenotificationpanel", FinalAttemptFailingMinimumPassingGradeNotificationPanel);
	app.component("mia-fixpaymentpage", defineAsyncComponent(() => import("@/ui/pages/FixPaymentPage.vue")));
	app.component("mia-floatinghelpbutton", FloatingHelpButton);
	app.component("mia-forgotpasswordparentpage", ForgotPasswordParentPage);
	app.component("mia-forgotpasswordstudentpage", ForgotPasswordStudentPage);
	app.component("mia-freshdesk", Freshdesk);
	app.component("mia-funds", Funds);
	app.component("mia-gameheader", GameHeader);
	app.component("mia-giftcodegenerator", GiftCodeGenerator);
	app.component("mia-glyphiconmenu", GlyphiconMenu);
	app.component("mia-gradebookroot", GradebookRoot);
	app.component("mia-guestbooklist", GuestBookList);
	app.component("mia-header", Header);
	app.component("mia-healthpage", defineAsyncComponent(() => import("@/Control/Vue/HealthPage.vue")));
	app.component("mia-i18next", I18NextStoreRegistration);
	app.component("mia-iframe", Iframe);
	app.component("mia-informationbanner", InformationBanner);
	app.component("mia-javascripterrortester", JavaScriptErrorTester);
	app.component("mia-klaviyoformplaceholder", KlaviyoFormPlaceholder);
	app.component("mia-klaviyosync", KlaviyoSync);
	app.component("mia-landingpage", LandingPage);
	app.component("mia-learningpathassignments", LearningPathAssignments);
	app.component("mia-learningpathcompleted", LearningPathCompleted);
	app.component("mia-legacylevelupscreen", LevelUpScreen);
	app.component("mia-lesson", Lesson);
	app.component("mia-lessonfeedback", LessonFeedback);
	app.component("mia-lessongameheader", LessonGameHeader);
	app.component("mia-lessonsroot", LessonsRoot);
	app.component("mia-liststoreregistration", ListStoreRegistration);
	app.component("mia-loginparentpage", LoginParentPage);
	app.component("mia-loginstudentpage", LoginStudentPage);
	app.component("mia-logo", Logo);
	app.component("mia-managebozomembers", ManageBozoMembers);
	app.component("mia-managemembershiproot", ManageMembershipRoot);
	app.component("mia-managepaymentmethod", ManagePaymentMethod);
	app.component("mia-memberblocklist", MemberUnblockList);
	app.component("mia-memberminiworld", MemberMiniworld);
	app.component("mia-membersearch", MemberSearch);
	app.component("mia-membershipdescription", MembershipDescription);
	app.component("mia-membershipfliptiles", MembershipFlipTiles);
	app.component("mia-membershipreceipts", MembershipReceipts);
	app.component("mia-miaprepannouncement", MiaPrepAnnouncement);
	app.component("mia-miaprepbanner", MiaPrepBanner);
	app.component("mia-miapreponlineacademyohiobanner", MiaprepOnlineAcademyOhioBanner);
	app.component("mia-miatour", MiaTour);
	app.component("mia-miatourstoreregistration", MiaTourStoreRegistration);
	app.component("mia-minimecloset", MiniMeCloset);
	app.component("mia-minimeclosetbanner", MiniMeClosetBanner);
	app.component("mia-miniworldhousecompetitionsidebar", MiniWorldHouseCompetitionSidebar);
	app.component("mia-modal", Modal);
	app.component("mia-moderatorsnote", ModeratorsNote);
	app.component("mia-morevideossection", MoreVideosSection);
	app.component("mia-myworldroot", MyWorldRoot);
	app.component("mia-navbarblog", NavbarBlog);
	app.component("mia-newsletterpreviewbanner", NewsletterPreviewBanner);
	app.component("mia-newspapereditionsidebar", NewspaperEditionSidebar);
	app.component("mia-newspaperpreviewsearch", NewspaperPreviewSearch);
	app.component("mia-notificationtesterheading", NotificationTesterHeading);
	app.component("mia-notifier", Notifier);
	app.component("mia-onlineschoolacademicplans", OnlineSchoolAcademicPlans);
	app.component("mia-onlineschoolloginselectpage", OnlineSchoolLoginSelectPage);
	app.component("mia-onlineschoolroot", OnlineSchoolRoot);
	app.component("mia-parentrevieweduseruploadedvideo", ParentReviewedUserUploadedVideo);
	app.component("mia-parentreviewuseruploadedvideo", ParentReviewUserUploadedVideo);
	app.component("mia-parentschildrenselector", ParentsChildrenSelector);
	app.component("mia-parentsnavbar", ParentsNavbar);
	app.component("mia-parentuseruploadedvideotermsmodal", ParentUserUploadedVideoTermsModal);
	app.component("mia-pausemembership", PauseMembership);
	app.component("mia-pausemembershipdashboard", PauseMembershipDashboard);
	app.component("mia-paymentconfirmedpage", defineAsyncComponent(() => import("@/ui/pages/PaymentConfirmedPage.vue")));
	app.component("mia-paymentrequiresattentionbanner", PaymentRequiresAttentionBanner);
	app.component("mia-practice", LessonPractice);
	app.component("mia-pricingeditor", PricingEditor);
	app.component("mia-pricingpanel", PricingPanel);
	app.component("mia-profileavatarbanner", ProfileAvatarBanner);
	app.component("mia-registermember", RegisterMember);
	app.component("mia-registrationconsentpage", RegistrationConsentPage);
	app.component("mia-registrationstudentselectionpage", RegistrationStudentSelectionPage);
	app.component("mia-regulatorsections", RegulatorSections);
	app.component("mia-reportsearch", ReportSearch);
	app.component("mia-routingaffinitytester", RoutingAffinityTester);
	app.component("mia-resourcelinksdropdown", ResourceLinksDropdown);
	app.component("mia-resourcelinksexpandable", ResourceLinksExpandable);
	app.component("mia-rewardandsocialfeaturespanel", RewardAndSocialFeaturesPanel);
	app.component("mia-rewardvimeoplayer", RewardVimeoPlayer);
	app.component("mia-rulesmodal", RulesModal);
	app.component("mia-scalableroutes", ScalableRoutes);
	app.component("mia-scoopcompetitionsidebar", ScoopCompetitionSidebar);
	app.component("mia-scopeandsequence", ScopeAndSequence);
	app.component("mia-scopeandsequencebutton", ScopeAndSequenceButton);
	app.component("mia-scopeandsequencepanel", ScopeAndSequencePanel);
	app.component("mia-scriptmanager", ScriptManager);
	app.component("mia-selecteditemstoreregistration", SelectedItemStoreRegistration);
	app.component("mia-shopcompetitionsidebar", ShopCompetitionSidebar);
	app.component("mia-shoutbox", ShoutBox);
	app.component("mia-singlesubjectmembershipdashboard", SingleSubjectMembershipDashboard);
	app.component("mia-siteabout", SiteAbout);
	app.component("mia-sitecontacttypeform", SiteContactTypeForm);
	app.component("mia-siteguidelines", SiteGuidelines);
	app.component("mia-siteimpressum", SiteImpressum);
	app.component("mia-siteslowwarning", SiteSlowWarning);
	app.component("mia-smileyselector", SmileySelector);
	app.component("mia-specialusers", SpecialUsers);
	app.component("mia-standardfooter", StandardFooter);
	app.component("mia-streakdashboard", StreakDashboard);
	app.component("mia-streakregistration", StreakRegistration);
	app.component("mia-streaksidebar", StreakSidebar);
	app.component("mia-studentannouncement", StudentAnnouncement);
	app.component("mia-studentelection", StudentElection);
	app.component("mia-subjectpreview", SubjectPreview);
	app.component("mia-taskcounterpanel", TaskCounterPanel);
	app.component("mia-teamachievementscompetitionsidebar", TeamAchievementsCompetitionSidebar);
	app.component("mia-teamsearch", TeamSearch);
	app.component("mia-testimonialspanel", TestimonialsPanel);
	app.component("mia-toggleablecontrol", ToggleableControl);
	app.component("mia-toggleablehelpboxwrapper", ToggleableHelpBoxWrapper);
	app.component("mia-togglepanel", TogglePanel);
	app.component("mia-transfertextresourcespreview", TransferTextResourcesPreview);
	app.component("mia-trialplanselectionpage", TrialPlanSelectionPage);
	app.component("mia-tutorialvideosplaybutton", TutorialVideosPlayButton);
	app.component("mia-typinggametouchdevices", TypingGameTouchDevices);
	app.component("mia-unfriendmemberlist", MemberUnfriendList);
	app.component("mia-unpausemembership", UnpauseMembership);
	app.component("mia-useruploadedvideos", UserUploadedVideos);
	app.component("mia-useruploadedvimeoplayer", UserUploadedVimeoPlayer);
	app.component("mia-videoroot", VideoRoot);
	app.component("mia-videowatchheading", VideoWatchHeading);
	app.component("mia-vimeoplayer", VimeoPlayer);
	app.component("mia-vimeouploadform", VimeoUploadForm);
	app.component("mia-virtualcurrencycompetition", VirtualCurrencyCompetition);
	app.component("mia-virtualcurrencymonitor", VirtualCurrencyMonitor);
	app.component("mia-vocabulary", Vocabulary);
	app.component("mia-vuegame", VueGame);
	app.component("mia-vuelist", VueList);
}
